// SessionFlow.jsx
// Location: src/components/SessionFlow.jsx
// Step-by-step practice session flow with progress tracking

import React from 'react';
import { ChevronLeft, ChevronRight, Clock, Music, Play } from 'lucide-react';
import { warmups } from '../data/DrumData.v2';

/**
 * Session Flow Component
 * Guides user through a practice session step-by-step
 * 
 * @param {string} sessionType - Type of session ('20min' | '10min')
 * @param {number} currentStep - Current step index (0-based)
 * @param {Object} routine - Today's routine { warmup, groove, song }
 * @param {Function} onNext - Callback when Continue/Complete button clicked
 * @param {Function} onExit - Callback when Back button clicked
 */
const SessionFlow = ({ 
  sessionType, 
  currentStep, 
  routine, 
  onNext, 
  onExit 
}) => {
  // Build steps array based on session type
  const getSteps = () => {
    if (!routine) return [];
    
    if (sessionType === '20min') {
      return [
        {
          title: 'Warm-up',
          duration: '5 min',
          item: routine.warmup,
          type: 'lesson'
        },
        {
          title: 'Groove Practice',
          duration: '7 min',
          item: routine.groove,
          type: 'lesson'
        },
        {
          title: 'Song Practice',
          duration: '8 min',
          item: routine.song,
          type: 'song'
        }
      ];
    } else {
      // 10-minute quick session
      return [
        {
          title: 'Quick Warm-up',
          duration: '3 min',
          item: warmups[1], // Use short warmup
          type: 'lesson'
        },
        {
          title: 'Song Focus',
          duration: '7 min',
          item: routine.song,
          type: 'song'
        }
      ];
    }
  };

  const steps = getSteps();
  const totalSteps = steps.length;
  
  // Guard against invalid state
  if (!routine || steps.length === 0 || currentStep >= steps.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center py-20 text-gray-400">
          Unable to load session. Please try again.
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={onExit}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-8 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Back
      </button>
      
      {/* Session Header */}
      <div className="text-center mb-8">
        <div className="flex items-center gap-2 justify-center mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Music className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-gray-800">
            {sessionType === '20min' ? 'Full Practice (20 min)' : 'Quick Session (10 min)'}
          </span>
        </div>
        <div className="text-sm text-gray-500">
          {currentStep + 1}/{totalSteps}
        </div>
      </div>
      
      {/* Progress Circle */}
      <div className="flex justify-center mb-8">
        <div className="relative w-32 h-32">
          {/* Background Circle */}
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            {/* Progress Circle */}
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#8b5cf6"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - (currentStep + 1) / totalSteps)}`}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          
          {/* Center Text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-800">{currentStep + 1}</div>
              <div className="text-xs text-gray-500">of {totalSteps}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Step Content Card */}
      <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 text-center">
        {/* Step Title */}
        <h2 className="text-3xl font-bold text-gray-800 mb-3">
          {currentStepData.title}
        </h2>
        
        {/* Duration */}
        <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
          <Clock className="w-5 h-5" />
          <span className="text-lg">{currentStepData.duration}</span>
        </div>
        
        {/* Item Details */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {currentStepData.item.title}
          </h3>
          {currentStepData.type === 'lesson' && (
            <p className="text-gray-600">{currentStepData.item.description}</p>
          )}
          {currentStepData.type === 'song' && (
            <p className="text-gray-600">
              {currentStepData.item.difficulty} • {currentStepData.item.tempo}
            </p>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Lesson Video Button */}
          {currentStepData.type === 'lesson' && (
            <a
              href={currentStepData.item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-all"
            >
              <Play className="w-5 h-5" />
              Watch Video
            </a>
          )}
          
          {/* Song Links */}
          {currentStepData.type === 'song' && (
            <>
              <a
                href={currentStepData.item.tutorialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all"
              >
                <Play className="w-5 h-5" />
                Tutorial
              </a>
              <a
                href={currentStepData.item.playalongUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all"
              >
                <Play className="w-5 h-5" />
                Play-along
              </a>
            </>
          )}
          
          {/* Continue / Complete Button */}
          <button
            onClick={onNext}
            className="w-full px-6 py-4 bg-gray-900 hover:bg-black text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
          >
            {isLastStep ? (
              <>Complete Session ✓</>
            ) : (
              <>
                Continue
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionFlow;